import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PaymentDialog } from "./PaymentDialog";
import { useAuth } from "../auth/AuthContext";
import { supabase } from "../lib/supabase";
import { categoryName, formatPrice, formatViews } from "../lib/catalog";
import type { Dorama } from "../types";
import { buildBunnyPlayerUrl } from "../lib/video";

type Media = {
  full_video_id: string | null;
  full_video_id_sub: string | null;
  full_video_id_dub: string | null;
};

type Episode = {
  id: string;
  title: string;
  episode_order: number;
  start_time: number;
};

export function DoramaPreviewModal({
  dorama,
  open,
  onClose,
}: {
  dorama: Dorama | null;
  open: boolean;
  onClose: () => void;
}) {
  const { user, loading: authLoading } = useAuth();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [media, setMedia] = useState<Media | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState<"dub" | "sub">("dub");
  const [startTime, setStartTime] = useState(0);
  const [paymentKind, setPaymentKind] = useState<"dorama" | "lifetime" | null>(null);
  const [vipOpen, setVipOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !paymentKind) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open, paymentKind]);

  useEffect(() => {
    if (!open || !dorama) return;
    let active = true;
    setLoading(true);
    setMedia(null);
    setEpisodes([]);
    setStartTime(0);
    setVipOpen(false);

    const episodesRequest = supabase
      .from("episodes")
      .select("id, title, episode_order, start_time")
      .eq("dorama_id", dorama.id)
      .order("episode_order", { ascending: true });
    const mediaRequest = supabase
      .from("dorama_media")
      .select("full_video_id, full_video_id_sub, full_video_id_dub")
      .eq("dorama_id", dorama.id)
      .maybeSingle();

    Promise.all([episodesRequest, mediaRequest]).then(([episodeResult, mediaResult]) => {
      if (!active) return;
      const nextMedia = (mediaResult.data ?? null) as Media | null;
      setEpisodes((episodeResult.data ?? []) as Episode[]);
      setMedia(nextMedia);
      if (!nextMedia?.full_video_id_dub) setLanguage("sub");
      setLoading(false);
    });

    return () => { active = false; };
  }, [dorama, open, user]);

  useEffect(() => { setPaymentKind(null); }, [dorama?.id, open]);

  const hasAccess = Boolean(media);
  const dubId = media?.full_video_id_dub ?? null;
  const subId = media?.full_video_id_sub || media?.full_video_id || null;
  const fullVideoId = language === "dub" && dubId ? dubId : subId || dubId;
  const videoId = hasAccess ? fullVideoId : dorama?.trailer_video_id || dorama?.trailer_url;
  const playerUrl = useMemo(() => {
    return buildBunnyPlayerUrl(videoId, { autoplay: true, startTime: hasAccess ? startTime : 0 });
  }, [hasAccess, startTime, videoId]);

  if (!open || !dorama) return null;

  const target = `/dorama/${dorama.slug || dorama.id}`;
  const unlock = () => {
    setPaymentKind("dorama");
  };

  const unlockVip = () => {
    setVipOpen(false);
    setPaymentKind("lifetime");
  };

  return (
    <div className="preview-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="preview-modal" role="dialog" aria-modal="true" aria-labelledby="preview-title">
        <button ref={closeButtonRef} className="preview-close" type="button" onClick={onClose} aria-label="Fechar prévia">×</button>

        <div className="preview-player">
          {playerUrl && (
            <iframe
              key={`${videoId}-${startTime}`}
              src={playerUrl}
              title={`${dorama.title} — ${hasAccess ? "vídeo completo" : "prévia"}`}
              loading="lazy"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          )}
          {!loading && !authLoading && !playerUrl && (
            <div className="preview-fallback">
              {dorama.hero_image_url || dorama.cover_url ? <img src={dorama.hero_image_url || dorama.cover_url || ""} alt="" /> : null}
              <span aria-hidden="true">▶</span>
              <p>Prévia em vídeo indisponível</p>
            </div>
          )}
        </div>

        <div className="preview-content">
          <h2 id="preview-title">{dorama.title}</h2>
          <div className="preview-meta" aria-label="Informações da novelinha">
            <span className="preview-rating">★ {dorama.rating ?? "—"}</span>
            <span>▣ {dorama.year ?? "—"}</span>
            <span>▥ {(dorama.episodes ?? episodes.length) || "—"} eps</span>
            <span>◉ {formatViews(dorama)}</span>
            <span className="preview-category">{categoryName(dorama.category)}</span>
          </div>
          <p className="preview-description">{dorama.description || "Sinopse não informada."}</p>

          {hasAccess && dubId && subId && (
            <div className="language-switch preview-language" aria-label="Idioma do vídeo">
              <button className={language === "dub" ? "active" : ""} onClick={() => { setLanguage("dub"); setStartTime(0); }}>Dublado</button>
              <button className={language === "sub" ? "active" : ""} onClick={() => { setLanguage("sub"); setStartTime(0); }}>Legendado</button>
            </div>
          )}

          {hasAccess && episodes.length > 0 && (
            <div className="preview-episodes">
              <strong>Episódios</strong>
              <div>{episodes.map((episode) => (
                <button key={episode.id} className={startTime === episode.start_time ? "active" : ""} onClick={() => setStartTime(episode.start_time)}>
                  {episode.episode_order}. {episode.title}
                </button>
              ))}</div>
            </div>
          )}

          {hasAccess ? (
            <Link className="preview-watch-button" to={target} onClick={onClose}>🎬 Assistir agora</Link>
          ) : (
            <>
              <button className="preview-unlock-button" type="button" onClick={unlock}>
                <strong>✧ DESBLOQUEAR NOVELA COMPLETA</strong>
                <span>Acesso imediato por apenas {formatPrice(dorama.price)}</span>
              </button>
              <div className="preview-divider"><span>ou</span></div>
              <button className="preview-vip-button" type="button" onClick={() => setPaymentKind("lifetime")}>
                <span className="preview-crown" aria-hidden="true">♕</span>
                <span><strong>Passe Vitalício — TODAS as novelas</strong><small>Pagamento único de R$ 27 · acesso para sempre</small></span>
                <b aria-hidden="true">→</b>
              </button>
            </>
          )}

          <Link className="preview-details-link" to={target} onClick={onClose}>Ver todos os detalhes →</Link>
        </div>
      </section>
      {paymentKind && <PaymentDialog doramaId={paymentKind === "dorama" ? dorama.id : undefined} price={paymentKind === "dorama" ? dorama.price : 27} onClose={() => setPaymentKind(null)} />}
      {vipOpen && (
        <div className="vip-offer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setVipOpen(false)}>
          <section className="vip-offer-modal" role="dialog" aria-modal="true" aria-labelledby="vip-offer-title">
            <button className="vip-offer-close" type="button" onClick={() => setVipOpen(false)} aria-label="Fechar oferta">×</button>
            <span className="vip-offer-crown" aria-hidden="true">♕</span>
            <h2 id="vip-offer-title">Passe Vitalício</h2>
            <p>Uma compra, acesso para sempre</p>
            <div className="vip-offer-price"><strong>R$ 27</strong><span>pagamento único — sem mensalidade</span></div>
            <ul>
              <li>Acesso a TODAS as novelinhas do catálogo</li>
              <li>Lançamentos futuros incluídos sem custo extra</li>
              <li>Qualidade Full HD com legendas e dublagem</li>
              <li>Assista no celular, tablet ou computador</li>
              <li>Sem anúncios, sem propagandas</li>
            </ul>
            <button className="vip-offer-cta" type="button" onClick={unlockVip}><span aria-hidden="true">♕</span> Desbloquear tudo agora</button>
            <small>🔒 Pagamento 100% seguro · PIX ou Cartão</small>
            <Link to="/vip" onClick={onClose}>Ver todos os detalhes do Passe</Link>
          </section>
        </div>
      )}
    </div>
  );
}
